<template>
  <AppTooltip :disabled="!iconOnly" location="right">
    <template #activator="{ props }">
      <div
        class="app-sidebar-item"
        :class="{
          'app-sidebar-item--nav': item.kind === 'nav',
          'app-sidebar-item--folder': item.kind === 'folder',
          'app-sidebar-item--section': item.kind === 'section',
          'app-sidebar-item--active': item.active,
          'app-sidebar-item--icon-only': iconOnly,
          'app-sidebar-item--clickable': isClickable,
        }"
        :data-locator="item.dataLocator"
        :style="computedStyle"
        :role="isClickable ? 'button' : undefined"
        :tabindex="isClickable ? 0 : undefined"
        :aria-current="item.active ? 'page' : undefined"
        v-bind="props"
        @click="onClick"
        @keydown.enter.prevent="onClick"
        @keydown.space.prevent="onClick"
      >
        <img
          v-if="item.iconSrc"
          :src="item.iconSrc"
          alt=""
          aria-hidden="true"
          class="app-sidebar-item__icon--image"
        />
        <app-material-icon
          v-else-if="item.icon"
          :name="item.icon"
          :filled="isFilledIcon"
          :color="resolvedIconColor"
          class="app-sidebar-item__icon"
          :class="{
            'app-sidebar-item__icon--nav': item.kind === 'nav' || item.kind === 'section',
            'app-sidebar-item__icon--folder': item.kind === 'folder',
          }"
        />

        <div v-if="!iconOnly" class="app-sidebar-item__label">
          <span>{{ item.label }}</span>
          <AppFeatureTag v-if="item.tag" :tag="item.tag" size="sm" />
        </div>
      </div>
    </template>
    <span>{{ item.label }}</span>
  </AppTooltip>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from 'vue'

import type { AppSidebarItemData } from '@/common/app-sidebar/appSidebar.types'
import AppFeatureTag from '@/common/AppFeatureTag.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import { FOLDER_ICON_COLOR } from '@/common/constants'

export default defineComponent({
  name: 'AppSidebarItem',
  components: { AppFeatureTag, AppTooltip },
  props: {
    item: {
      type: Object as PropType<AppSidebarItemData>,
      required: true,
    },
    iconOnly: {
      type: Boolean,
      default: false,
    },
  },
  inheritAttrs: false,
  emits: ['select'],
  setup (props, { emit }) {
    const isClickable = computed(() => props.item.kind !== 'section')

    const resolvedIconColor = computed(() => (
      props.item.iconColor || '#375576'
    ))
    const isFilledIcon = computed(() => (
      props.item.kind === 'folder' || !!props.item.iconFilled
    ))

    const computedStyle = computed(() => {
      if (props.iconOnly) {
        return undefined
      }

      if (props.item.kind !== 'folder') {
        return undefined
      }

      const depth = props.item.depth ?? 0

      return {
        paddingLeft: `${8 + depth * 8}px`,
      }
    })

    function onClick () {
      if (!isClickable.value) {
        return
      }

      emit('select', props.item.key)
    }

    return {
      FOLDER_ICON_COLOR,
      computedStyle,
      isFilledIcon,
      isClickable,
      onClick,
      resolvedIconColor,
    }
  },
})
</script>

<style scoped lang="scss">
@import "@/scss/border-tokens";

.app-sidebar-item {
  display: flex;
  align-items: center;
  width: 224px;
  max-width: 100%;
  height: 36px;
  padding: 8px;
  @include border-radius('border-radius-2');
  gap: 8px;
  opacity: 1;
  color: rgb(var(--v-theme-text-secondary));
  box-sizing: border-box;

  &--clickable {
    cursor: pointer;
  }

  &--nav:not(.app-sidebar-item--active):hover,
  &--folder:not(.app-sidebar-item--active):hover {
    background: rgba(var(--v-theme-surface-hover));
  }

  &--active {
    background: #EDF0F2;
  }

  &--section {
    background: transparent;
  }

  &--folder:not(.app-sidebar-item--active) {
    background: transparent;
  }

  &--icon-only {
    width: 36px;
    min-width: 36px;
  }

  &__icon {
    flex: 0 0 auto;

    &--nav {
      color: #3c5d86;
    }

    &--folder {
      color: v-bind(FOLDER_ICON_COLOR);
    }

    &--image {
      width: 20px;
      height: 20px;
    }
  }

  &__label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    color: rgb(var(--v-theme-text-secondary));
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0;
    overflow: hidden;
    white-space: nowrap;

    span {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}
</style>
